import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ShieldAlert, Loader2, Scissors, CheckCircle2, Lock,
  RefreshCw, FolderTree, Info,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Emergency Admin — admin-only "break glass" actions for when the normal
 * gated workflow blocks production. Right now hosts the Emergency CBA
 * runner; future emergency actions slot in as additional cards.
 *
 * Founder rule: when an emergency action runs, the system audit-stamps it
 * AND auto-shorts-circuits the matching normal flow once the upstream gate
 * (e.g. client approval on the animatic) eventually fires.
 */
export default function EmergencyAdminPage() {
  const { user } = useAuth();
  if (user?.role !== 'master_admin') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="w-12 h-12 text-rose-400 mx-auto mb-3" />
          <p className="text-sm text-zinc-300">Master Admin only — break-glass authority.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-8" data-testid="emergency-admin-page">
      <header className="mb-8 flex items-start gap-4 max-w-5xl">
        <div className="w-12 h-12 rounded-md bg-rose-500/15 border border-rose-500/40 text-rose-300 flex items-center justify-center">
          <ShieldAlert className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h1 className="text-3xl font-heading font-semibold tracking-tight">Emergency · Break-Glass Admin</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Bypass routine gates when production cannot wait. Every action here is audit-logged
            and short-circuits the matching normal flow if its upstream gate fires later.
          </p>
        </div>
      </header>

      <div className="max-w-5xl space-y-6">
        <EmergencyCBACard />
        <ManualCBAIngestCard />
      </div>
    </div>
  );
}


function EmergencyCBACard() {
  const { api } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [autoPickDir, setAutoPickDir] = useState('');
  const [sourceRel, setSourceRel] = useState('');
  const [threshold, setThreshold] = useState(0.4);
  const [softThreshold, setSoftThreshold] = useState(0.1);
  const [minShotSec, setMinShotSec] = useState(0.2);
  const [snapToSilence, setSnapToSilence] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [lockAfter, setLockAfter] = useState(true);
  const [extractAfter, setExtractAfter] = useState(true);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [job, setJob] = useState(null);  // live job state from /job/:id polling

  // Load projects for the dropdown
  useEffect(() => {
    api.get('/projects').then(r => {
      setProjects(Array.isArray(r.data) ? r.data : (r.data?.projects || []));
    }).catch(() => setProjects([]));
  }, [api]);

  // Load existing CBA status whenever the project changes
  const refreshStatus = useMemo(() => async (pid) => {
    if (!pid) { setStatus(null); return; }
    setStatusBusy(true);
    try {
      const r = await api.get(`/cba/admin/emergency/status/${pid}`);
      setStatus(r.data || null);
    } catch (e) {
      setStatus(null);
    } finally {
      setStatusBusy(false);
    }
  }, [api]);

  useEffect(() => {
    if (projectId) refreshStatus(projectId);
    else setStatus(null);
  }, [projectId, refreshStatus]);

  // Poll the running job every 2s while it's active. Stops once phase
  // becomes done or error. Runs in a separate effect tied to job.id so
  // we don't double-poll a stale job after a new run is kicked off.
  useEffect(() => {
    if (!job?.id || job.phase === 'done' || job.phase === 'error') return;
    const tid = setInterval(async () => {
      try {
        const r = await api.get(`/cba/admin/emergency/job/${job.id}`);
        setJob(r.data || null);
        if (r.data?.phase === 'done' || r.data?.phase === 'error') {
          setBusy(false);
          if (r.data?.phase === 'done') {
            setResult(r.data.result);
          } else {
            setErr(r.data.error || 'Pipeline failed');
          }
          await refreshStatus(projectId);
        }
      } catch (e) { /* keep polling */ }
    }, 2000);
    return () => clearInterval(tid);
  }, [job?.id, job?.phase, api, projectId, refreshStatus]);

  const run = async () => {
    setBusy(true); setErr(''); setResult(null); setJob(null);
    try {
      const body = {
        project_id: projectId,
        threshold: parseFloat(threshold) || 0.4,
        soft_threshold: parseFloat(softThreshold) || 0.1,
        min_shot_sec: parseFloat(minShotSec) || 0.2,
        snap_to_silence: !!snapToSilence,
        lock: !!lockAfter,
        extract: !!extractAfter,
        verify: !!extractAfter,
      };
      if (autoPickDir.trim()) body.auto_pick_dir = autoPickDir.trim();
      if (sourceRel.trim()) body.source_rel = sourceRel.trim();
      const r = await api.post('/cba/admin/emergency/run', body);
      // Endpoint returns 202 with {job_id, sequence_task_id, status_url} —
      // start polling. Poller (above) will flip busy/result/err on done/error.
      if (r.data?.job_id) {
        setJob({ id: r.data.job_id, phase: 'queued', phase_label: 'Queued', progress_pct: 0 });
      } else {
        // Backwards-compat: old sync endpoint
        setResult(r.data);
        setBusy(false);
        await refreshStatus(projectId);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Emergency CBA failed');
      setBusy(false);
    }
  };

  const alreadyRun = !!status?.cba_emergency_used;
  const isLocked = !!status?.cba_locked;

  const forceReset = async () => {
    if (!window.confirm(
      `⚠️ FORCE RESET will WIPE all CBA data on this project:\n\n` +
      `  · ${status?.clip_count || 0} clips\n` +
      `  · ${status?.shot_task_count || 0} Layout shot tasks (and downstream stages)\n` +
      `  · all cba_extract_jobs + shot_registry placeholders\n\n` +
      `Use this only if the previous emergency run was wrong (e.g. detected fake heuristic clips on old daemon).\n\n` +
      `Continue?`,
    )) return;
    setBusy(true); setErr(''); setResult(null);
    try {
      const r = await api.post('/cba/admin/emergency/force-reset', {
        project_id: projectId, confirm: true,
      });
      setResult({ message: r.data?.message, force_reset: true, ...r.data });
      await refreshStatus(projectId);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Force reset failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-md border border-rose-500/30 bg-gradient-to-br from-rose-950/30 via-zinc-950 to-zinc-950 overflow-hidden"
             data-testid="emergency-cba-card">
      {/* Header */}
      <div className="px-5 py-4 border-b border-rose-500/20 flex items-center gap-3">
        <div className="w-10 h-10 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30 flex items-center justify-center">
          <Scissors className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-heading font-semibold text-rose-200">Emergency CBA · Cut-By-Animatics</h2>
          <p className="text-[11px] text-zinc-400">
            Reads latest animatic from <span className="font-mono text-rose-300">01_Pre_Production/003_Animatics/</span>,
            detects every camera cut via FFmpeg on NAS, writes <span className="font-mono text-rose-300">SH_NNN/</span> folders
            (video + WAV) into <span className="font-mono text-rose-300">004_Cut_by_Animatics/</span>, then verifies on disk.
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-5 space-y-5">
        {/* Project picker */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Project *</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)}
                    data-testid="emergency-cba-project"
                    className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:border-rose-500/40 focus:outline-none">
              <option value="">— Pick project —</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name || p.code || p.id}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Animatics folder on NAS (optional)</label>
            <input type="text" value={autoPickDir} onChange={e => setAutoPickDir(e.target.value)}
                   data-testid="emergency-cba-auto-pick-dir"
                   placeholder="auto · resolves to <project>/01_Pre_Production/003_Animatics"
                   className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm font-mono text-[11px] focus:border-rose-500/40 focus:outline-none" />
            <p className="text-[10px] text-zinc-500 mt-1">Leave empty → auto-resolves from project's NAS mapping. Daemon picks newest video file in this folder.</p>
          </div>
        </div>

        {/* Source path override */}
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Specific animatic file (optional override)</label>
          <input type="text" value={sourceRel} onChange={e => setSourceRel(e.target.value)}
                 data-testid="emergency-cba-source-rel"
                 placeholder="auto · daemon picks newest .mp4/.mov in 003_Animatics"
                 className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm font-mono text-[11px] focus:border-rose-500/40 focus:outline-none" />
          <p className="text-[10px] text-zinc-500 mt-1">If set, scene-detect runs on this exact file instead of newest.</p>
        </div>

        {/* Sensitivity */}
        <div className="rounded border border-zinc-800 bg-zinc-900/40 p-3 space-y-3">
          <p className="text-[11px] text-zinc-400 flex items-center gap-2">
            <Info className="w-3.5 h-3.5" /> Detection settings. v3 engine: 3-pass (hard cuts + soft/dissolve cuts + audio silence snap).
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Hard cut threshold</label>
              <input type="number" step="0.05" min="0.1" max="0.9" value={threshold} onChange={e => setThreshold(e.target.value)}
                     data-testid="emergency-cba-threshold"
                     className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
              <p className="text-[10px] text-zinc-500 mt-0.5">0.4 = recommended. Higher = stricter.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Soft / dissolve threshold</label>
              <input type="number" step="0.02" min="0.0" max="0.4" value={softThreshold} onChange={e => setSoftThreshold(e.target.value)}
                     data-testid="emergency-cba-soft-threshold"
                     className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
              <p className="text-[10px] text-zinc-500 mt-0.5">0.1 catches fades. 0 to disable.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Min shot length (sec)</label>
              <input type="number" step="0.1" min="0.05" max="2.0" value={minShotSec} onChange={e => setMinShotSec(e.target.value)}
                     data-testid="emergency-cba-min-shot-sec"
                     className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
              <p className="text-[10px] text-zinc-500 mt-0.5">0.2 keeps quick cuts.</p>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-[12px] cursor-pointer select-none w-full">
                <input type="checkbox" checked={snapToSilence} onChange={e => setSnapToSilence(e.target.checked)}
                       data-testid="emergency-cba-snap-silence"
                       className="w-4 h-4 rounded accent-rose-500" />
                <span>Snap cuts to dialogue boundaries (audio silence)</span>
              </label>
            </div>
          </div>
        </div>

        {/* Toggles */}
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-[12px] cursor-pointer select-none">
            <input type="checkbox" checked={lockAfter} onChange={e => setLockAfter(e.target.checked)}
                   data-testid="emergency-cba-lock-after"
                   className="w-4 h-4 rounded accent-rose-500" />
            <Lock className="w-3.5 h-3.5 text-rose-300" />
            Lock and fan-out shot tasks (Layout → Animation → Lighting → FX → Comp → Render).
          </label>
          <label className="flex items-center gap-2 text-[12px] cursor-pointer select-none">
            <input type="checkbox" checked={extractAfter} onChange={e => setExtractAfter(e.target.checked)}
                   data-testid="emergency-cba-extract-after"
                   className="w-4 h-4 rounded accent-rose-500" />
            <Scissors className="w-3.5 h-3.5 text-rose-300" />
            Extract MP4 + WAV per shot to <span className="font-mono">004_Cut_by_Animatics/</span> AND verify on NAS.
          </label>
        </div>

        {/* Existing status pill */}
        {projectId && (
          <div className="rounded border border-zinc-800 bg-zinc-900/40 p-3 text-[11px]" data-testid="emergency-cba-status">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">Current state on this project</span>
              <button onClick={() => refreshStatus(projectId)} className="text-zinc-500 hover:text-white" title="Refresh">
                <RefreshCw className={`w-3 h-3 ${statusBusy ? 'animate-spin' : ''}`} />
              </button>
            </div>
            {!status?.exists ? (
              <p className="text-zinc-500">No CBA / animatics sequence task on this project yet — it will be auto-created when you run.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <StatRow k="Sequence" v={status.title || status.sequence_task_id} mono />
                <StatRow k="Clips" v={`${status.clip_count}${status.cba_locked ? ' (locked)' : ''}`} />
                <StatRow k="Layout shot tasks" v={status.shot_task_count} />
                <StatRow k="Emergency used" v={status.cba_emergency_used ? `Yes · ${(status.cba_emergency_at||'').slice(0,16)}` : 'No'} />
                <StatRow k="Emergency by" v={status.cba_emergency_by_email || '—'} />
                <StatRow k="Bridge at run" v={status.cba_emergency_bridge_status || '—'} />
                <StatRow k="Client approved" v={status.client_approved ? `Yes · ${(status.client_approved_at||'').slice(0,16)}` : 'No (verbal only)'} />
              </div>
            )}
          </div>
        )}

        {/* Already-done short-circuit notice */}
        {alreadyRun && status?.client_approved && (
          <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-3 text-[11px] text-emerald-200 flex items-start gap-2"
               data-testid="emergency-cba-already-done-banner">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Normal flow short-circuited.</p>
              <p className="text-[10px] text-emerald-300/80 mt-0.5">
                CBA was already executed via Emergency on {(status.cba_emergency_at||'').slice(0,16)} —
                {status.clip_count} clips, {status.shot_task_count} Layout tasks. Client has now formally approved the animatic
                ({(status.client_approved_at||'').slice(0,16)}) — the system has marked the emergency as acknowledged.
                <strong className="text-emerald-100"> No further action needed.</strong>
              </p>
            </div>
          </div>
        )}

        {/* Errors */}
        {err && (
          <div className="rounded border border-rose-500/30 bg-rose-500/10 p-3 text-[11px] text-rose-300 flex items-start gap-2"
               data-testid="emergency-cba-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="whitespace-pre-wrap break-words">{err}</div>
          </div>
        )}

        {/* Locked-state notice */}
        {isLocked && projectId && (
          <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-200 flex items-start gap-2"
               data-testid="emergency-cba-locked-notice">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-amber-100">CBA is currently locked.</p>
              <p className="text-[10px] text-amber-300/80 mt-0.5">
                {status?.cba_emergency_used && status?.cba_emergency_bridge_status?.includes('error: unknown action')
                  ? 'These clips were generated by an OLD daemon that didn\'t understand scene_detect — the cloud fell back to the heuristic and locked the seq. Click Force Reset below to wipe + re-run with the new daemon.'
                  : 'A previous emergency or normal CBA run already locked this seq. Use Force Reset to wipe and re-run.'}
              </p>
            </div>
          </div>
        )}

        {/* Live progress bar — visible while a job is running */}
        {job && job.phase !== 'done' && job.phase !== 'error' && (
          <div className="rounded border border-cyan-500/30 bg-cyan-500/5 p-4 text-[11px]" data-testid="emergency-cba-progress">
            <div className="flex items-center gap-2 mb-2">
              <Loader2 className="w-4 h-4 animate-spin text-cyan-300" />
              <p className="font-medium text-cyan-100">{job.phase_label || job.phase}</p>
              <span className="ml-auto text-cyan-300 font-mono">{job.progress_pct}%</span>
            </div>
            <div className="w-full bg-zinc-900 rounded-full h-2 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500"
                   style={{ width: `${job.progress_pct || 0}%` }} />
            </div>
            <p className="text-[10px] text-cyan-300/70 mt-2 font-mono">
              Job ID: {job.id?.slice(0, 8)} · Phase: <span className="text-cyan-300">{job.phase}</span>
              {job.stats?.cuts_detected ? ` · ${job.stats.cuts_detected} cuts detected` : ''}
            </p>
          </div>
        )}

        {/* Run + Force Reset row */}
        <div className="pt-2 flex flex-wrap items-center gap-3">
          <button onClick={run} disabled={!projectId || busy || isLocked}
                  data-testid="emergency-cba-run-btn"
                  className="px-5 py-2.5 rounded bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-400 hover:to-orange-400 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-40 shadow-lg shadow-rose-500/20">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scissors className="w-4 h-4" />}
            {busy ? (job?.phase_label || 'Running…') : `Run Emergency CBA${extractAfter ? ' + Extract + Verify' : ''}`}
          </button>
          {isLocked && (
            <button onClick={forceReset} disabled={busy}
                    data-testid="emergency-cba-force-reset-btn"
                    className="px-4 py-2.5 rounded border border-amber-500/40 hover:border-amber-400 text-amber-200 hover:text-amber-100 text-sm font-medium flex items-center gap-2 disabled:opacity-40">
              <ShieldAlert className="w-4 h-4" />
              Force Reset (wipe locked CBA)
            </button>
          )}
          <p className="text-[10px] text-zinc-500 flex items-center gap-1">
            <FolderTree className="w-3 h-3" /> Audit-logged · master_admin only · may take 5-15 min
          </p>
        </div>

        {/* Result — rich, verifies on NAS */}
        {result && (
          <div className={`rounded border p-4 text-[11px] ${
            result.verification?.performed && result.verification?.found_on_nas === result.clip_count
              ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-200'
              : 'border-amber-500/30 bg-amber-500/5 text-amber-200'
          }`} data-testid="emergency-cba-result">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4" />
              <p className="font-medium text-zinc-100 break-all">{result.summary || result.message}</p>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <StatRow k="Animatic" v={result.nas_source_rel} mono />
              <StatRow k="Duration" v={result.duration_sec ? `${result.duration_sec.toFixed(1)}s` : '—'} />
              <StatRow k="Output folder" v={result.out_rel} mono />
              <StatRow k="Cuts detected" v={result.clip_count} />
              <StatRow k="Pipeline tasks" v={result.tasks_created || 0} />
              <StatRow k="Bridge transport" v={result.transport || '—'} mono />
              {result.scene_stats && (
                <>
                  <StatRow k="Hard cuts" v={result.scene_stats.hard_cuts ?? '—'} />
                  <StatRow k="Soft / dissolve cuts" v={result.scene_stats.soft_cuts ?? '—'} />
                  <StatRow k="Audio silence intervals" v={result.scene_stats.silence_intervals ?? '—'} />
                  <StatRow k="Cuts snapped to silence" v={result.scene_stats.snapped_to_silence ?? '—'} />
                </>
              )}
              {result.extract_attempted && (
                <>
                  <StatRow k="Shots extracted" v={`${result.extract_ok}/${result.clip_count}`} />
                  <StatRow k="Extract failures" v={result.extract_failed || 0} />
                </>
              )}
              {result.verification?.performed && (
                <>
                  <StatRow k="Verified on NAS" v={`${result.verification.found_on_nas}/${result.verification.expected}`} />
                  {!!result.verification.missing_on_nas?.length && (
                    <StatRow k="Missing folders" v={result.verification.missing_on_nas.slice(0, 5).join(', ') + (result.verification.missing_on_nas.length > 5 ? ` … +${result.verification.missing_on_nas.length - 5} more` : '')} mono />
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}


function StatRow({ k, v, mono }) {
  return (
    <div className="flex items-baseline gap-2 text-[11px]">
      <span className="text-zinc-500 uppercase tracking-wider text-[9px] w-32 flex-shrink-0">{k}</span>
      <span className={`${mono ? 'font-mono text-[10px]' : ''} text-zinc-200 break-all`}>{v ?? '—'}</span>
    </div>
  );
}


/* ─── MANUAL CBA INGEST CARD ──────────────────────────────────────────
   Founder spec (May 13, 2026): when the editor cuts shots by hand and
   drops SH### folders into <project>/01_Pre_Production/004_Cut_by_Animatics/
   on the NAS, one click here:
     1. scans the folder via the NAS bridge
     2. creates cba_clips + shot_registry rows for every SH-folder
        that contains a video file
     3. fans out the 6-stage Layout→Render pipeline (Layout in
        "unassigned" so PMs can immediately allot)
     4. marks the parent CBA seq task as cba_locked + completed
   ─────────────────────────────────────────────────────────────────────*/
function ManualCBAIngestCard() {
  const { api } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/projects').then(r => {
      setProjects(Array.isArray(r.data) ? r.data : (r.data?.projects || []));
    }).catch(() => setProjects([]));
  }, [api]);

  const run = async () => {
    if (!projectId) return;
    setBusy(true); setErr(''); setResult(null);
    try {
      const r = await api.post(`/cba/manual-ingest/${projectId}`);
      setResult(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Manual ingest failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-zinc-900 border border-emerald-500/40 rounded-lg p-5" data-testid="manual-cba-ingest-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 flex items-center justify-center">
          <FolderTree className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-heading font-semibold text-emerald-200">Manual CBA · NAS Folder Scan</h2>
          <p className="text-sm text-zinc-400 mt-1">
            For when the editor cuts shots by hand and drops <span className="font-mono text-emerald-300">SH###</span> folders
            into <span className="font-mono text-zinc-300">01_Pre_Production/004_Cut_by_Animatics/</span> on the NAS.
            One click scans the folder, creates one Layout + Animation + Lighting + FX + Comp + Render task
            per shot, and marks CBA complete — ready for allotment.
          </p>
        </div>
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <label className="flex-1 min-w-[200px]">
          <span className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Project</span>
          <select
            value={projectId}
            onChange={e => { setProjectId(e.target.value); setResult(null); setErr(''); }}
            data-testid="manual-cba-project-select"
            className="w-full h-9 bg-zinc-950 border border-zinc-700 rounded text-xs text-zinc-200 px-3"
          >
            <option value="">Select project…</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </label>
        <button
          onClick={run}
          disabled={busy || !projectId}
          data-testid="manual-cba-scan-btn"
          className="h-9 px-4 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-bold rounded flex items-center gap-2 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderTree className="w-3.5 h-3.5" />}
          {busy ? 'Scanning NAS…' : 'Scan NAS for Manual Cuts'}
        </button>
      </div>

      {err && (
        <div className="mt-4 bg-red-500/10 border border-red-500/40 text-red-200 rounded-md p-3 text-[11px]" data-testid="manual-cba-err">
          <AlertTriangle className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />
          {err}
        </div>
      )}

      {result && (
        <div className="mt-4 space-y-2 bg-emerald-500/5 border border-emerald-500/20 rounded-md p-4" data-testid="manual-cba-result">
          {result.ok ? (
            <>
              <p className="text-emerald-300 text-sm font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                {result.clips_created > 0
                  ? `Created ${result.clips_created} cba_clips · ${result.tasks_created} pipeline tasks · ${result.ready_for_allotment_count} shots ready for allotment`
                  : `No new clips created — all ${result.clips_skipped_existing} folders were already ingested.`}
              </p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[11px] mt-3">
                <StatRow k="NAS Path" v={result.cba_dir} mono />
                <StatRow k="Folders scanned" v={result.folders_scanned} />
                <StatRow k="SH-folders found" v={result.sh_folders_found} />
                <StatRow k="Skipped (no media)" v={result.clips_skipped_no_media_count} />
                <StatRow k="Skipped (existing)" v={result.clips_skipped_existing} />
                <StatRow k="Tasks created" v={result.tasks_created} />
                <StatRow k="Tasks skipped (already)" v={result.tasks_skipped} />
                <StatRow k="Seq task" v={result.sequence_task_id} mono />
              </div>
              {result.ready_for_allotment && result.ready_for_allotment.length > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-500/20">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1">Ready for allotment</p>
                  <p className="text-[10px] text-zinc-400 font-mono leading-relaxed">
                    {result.ready_for_allotment.join(' · ')}
                    {result.ready_for_allotment_count > result.ready_for_allotment.length && (
                      <span className="text-zinc-600"> … and {result.ready_for_allotment_count - result.ready_for_allotment.length} more</span>
                    )}
                  </p>
                </div>
              )}
              {result.clips_skipped_no_media_count > 0 && (
                <div className="mt-3 pt-3 border-t border-amber-500/20">
                  <p className="text-[10px] uppercase tracking-wider text-amber-400 mb-1">
                    ⚠️ Skipped — no video file in folder
                  </p>
                  <p className="text-[10px] text-zinc-400 font-mono leading-relaxed">
                    {result.clips_skipped_no_media.join(' · ')}
                    {result.clips_skipped_no_media_count > result.clips_skipped_no_media.length && (
                      <span className="text-zinc-600"> … and {result.clips_skipped_no_media_count - result.clips_skipped_no_media.length} more</span>
                    )}
                  </p>
                  <p className="text-[10px] text-zinc-500 italic mt-1">
                    These folders had no .mp4/.mov inside. Drop a video file in and re-run.
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="text-red-300 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {result.error || 'NAS bridge unreachable'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
